import React, { useState } from 'react';
import SpaceBetween from '@cloudscape-design/components/space-between';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import AuthCard from '../components/AuthCard';
import { getAuthService } from '../services';
import { isValidEmailFormat, validatePassword } from '../services/validation';

interface ForgotPasswordScreenProps {
    onBack: () => void;
}

type Step = 'request' | 'confirm' | 'done';

/**
 * Self-service password reset (code-based). Step 1 requests a code, which the
 * service emails to the account. Step 2 takes that code plus a new password.
 * Sign-in still uses email OTP MFA — this reset flow is a separate email code.
 */
export default function ForgotPasswordScreen({ onBack }: ForgotPasswordScreenProps) {
    const [step, setStep] = useState<Step>('request');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const requestCode = async () => {
        setError(null);
        setNotice(null);
        if (!isValidEmailFormat(email)) {
            setError('Enter a valid email address.');
            return;
        }
        setSubmitting(true);
        try {
            await getAuthService().resetPassword(email.trim());
            setNotice(`If an account exists for ${email.trim()}, a reset code is on its way.`);
            setStep('confirm');
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not start the reset.');
        } finally {
            setSubmitting(false);
        }
    };

    const submitNewPassword = async () => {
        setError(null);
        setNotice(null);
        if (!code.trim()) {
            setError('Enter the code we emailed you.');
            return;
        }
        const pwErr = validatePassword(password);
        if (pwErr) {
            setError(pwErr);
            return;
        }
        if (password !== confirm) {
            setError('Passwords do not match.');
            return;
        }
        setSubmitting(true);
        try {
            await getAuthService().confirmResetPassword(email.trim(), code.trim(), password);
            setStep('done');
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not reset your password.');
        } finally {
            setSubmitting(false);
        }
    };

    if (step === 'done') {
        return (
            <AuthCard title="Password updated" subtitle="You can now sign in with your new password.">
                <SpaceBetween size="l">
                    <Alert type="success">
                        Your password has been reset. Sign in with your new password and the
                        verification code we email you.
                    </Alert>
                    <Button variant="primary" onClick={onBack}>
                        Back to sign in
                    </Button>
                </SpaceBetween>
            </AuthCard>
        );
    }

    if (step === 'confirm') {
        return (
            <AuthCard
                title="Set a new password"
                subtitle={`Enter the code we emailed to ${email.trim()}.`}
            >
                <SpaceBetween size="l">
                    <FormField label="Verification code">
                        <Input
                            type="text"
                            value={code}
                            onChange={({ detail }) => setCode(detail.value)}
                        />
                    </FormField>
                    <FormField label="New password">
                        <Input
                            type="password"
                            value={password}
                            onChange={({ detail }) => setPassword(detail.value)}
                        />
                    </FormField>
                    <FormField label="Confirm new password">
                        <Input
                            type="password"
                            value={confirm}
                            onChange={({ detail }) => setConfirm(detail.value)}
                        />
                    </FormField>
                    {notice ? <Alert type="info">{notice}</Alert> : null}
                    {error ? <Alert type="error">{error}</Alert> : null}
                    <Button variant="primary" loading={submitting} onClick={submitNewPassword}>
                        Update password
                    </Button>
                    <Button variant="link" disabled={submitting} onClick={() => setStep('request')}>
                        Use a different email
                    </Button>
                    <Button variant="link" onClick={onBack}>
                        Back to sign in
                    </Button>
                </SpaceBetween>
            </AuthCard>
        );
    }

    return (
        <AuthCard title="Forgot password" subtitle="We'll email you a code to reset it.">
            <SpaceBetween size="l">
                <FormField
                    label="Email"
                    description="Enter your account email. Signing in afterward still requires the emailed verification code."
                >
                    <Input
                        type="email"
                        value={email}
                        autoComplete
                        onChange={({ detail }) => setEmail(detail.value)}
                    />
                </FormField>
                {notice ? <Alert type="info">{notice}</Alert> : null}
                {error ? <Alert type="error">{error}</Alert> : null}
                <Button variant="primary" loading={submitting} onClick={requestCode}>
                    Send reset code
                </Button>
                <Button variant="link" onClick={onBack}>
                    Back to sign in
                </Button>
            </SpaceBetween>
        </AuthCard>
    );
}
